import React, { useEffect, useState } from "react";
import { Settings2, X, Sun, Layers, Play, Download, Loader2, MapPin, Save, Check, Sparkles, Eye, EyeOff, Ruler, Square as SquareIcon, RotateCcw, Film } from "lucide-react";
import { geocodeZip } from "../lib/api";
import useDraggable from "../hooks/useDraggable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// US state codes for the address-string parser. Anything outside this set is
// treated as part of the city name so we don't accidentally clobber it.
const US_STATE_CODES = new Set([
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN",
  "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH",
  "NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT",
  "VT","VA","WA","WV","WI","WY",
]);
const US_STATE_NAME_TO_CODE = {
  alabama:"AL", alaska:"AK", arizona:"AZ", arkansas:"AR", california:"CA",
  colorado:"CO", connecticut:"CT", delaware:"DE", "district of columbia":"DC",
  florida:"FL", georgia:"GA", hawaii:"HI", idaho:"ID", illinois:"IL",
  indiana:"IN", iowa:"IA", kansas:"KS", kentucky:"KY", louisiana:"LA",
  maine:"ME", maryland:"MD", massachusetts:"MA", michigan:"MI", minnesota:"MN",
  mississippi:"MS", missouri:"MO", montana:"MT", nebraska:"NE", nevada:"NV",
  "new hampshire":"NH", "new jersey":"NJ", "new mexico":"NM", "new york":"NY",
  "north carolina":"NC", "north dakota":"ND", ohio:"OH", oklahoma:"OK",
  oregon:"OR", pennsylvania:"PA", "rhode island":"RI", "south carolina":"SC",
  "south dakota":"SD", tennessee:"TN", texas:"TX", utah:"UT", vermont:"VT",
  virginia:"VA", washington:"WA", "west virginia":"WV", wisconsin:"WI",
  wyoming:"WY",
};

/**
 * Best-effort parser for a free-form US address string into {city, state, zip}.
 * Recognises forms like:
 *   "Boulder, CO 80301"
 *   "Boulder CO 80301"
 *   "123 Main St, Boulder, Colorado 80301"
 *   "80301"
 * Returns null if it cannot identify at least two of the three components.
 */
export function parseAddressString(input) {
  if (!input || typeof input !== "string") return null;
  const s = input.trim();
  if (!s) return null;

  // 1. Pull a 5-digit ZIP (with optional +4) out of the string.
  const zipMatch = s.match(/\b(\d{5})(?:-\d{4})?\b/);
  const zip = zipMatch ? zipMatch[1] : "";
  let rest = zip ? s.replace(zipMatch[0], " ") : s;
  rest = rest.replace(/\s{2,}/g, " ").replace(/[,\s]+$/g, "").trim();

  // 2. Look for a 2-letter state code at the tail.
  let state = "";
  const codeTail = rest.match(/(?:^|[,\s])([A-Za-z]{2})\s*$/);
  if (codeTail && US_STATE_CODES.has(codeTail[1].toUpperCase())) {
    state = codeTail[1].toUpperCase();
    rest = rest.slice(0, codeTail.index).replace(/[,\s]+$/g, "").trim();
  }
  // 3. Or a full state name at the tail.
  if (!state) {
    const lower = rest.toLowerCase();
    for (const [name, code] of Object.entries(US_STATE_NAME_TO_CODE)) {
      if (lower.endsWith(name) && /[,\s]/.test(lower.charAt(lower.length - name.length - 1) || " ")) {
        state = code;
        rest = rest.slice(0, rest.length - name.length).replace(/[,\s]+$/g, "").trim();
        break;
      }
      if (lower === name) {
        state = code;
        rest = "";
        break;
      }
    }
  }

  // 4. Whatever's left is the city. If it has commas, the LAST comma-piece is
  //    the city (so "123 Main St, Boulder" → "Boulder").
  let city = rest;
  if (city.includes(",")) {
    const parts = city.split(",").map((p) => p.trim()).filter(Boolean);
    city = parts[parts.length - 1] || "";
  }

  const found = [city, state, zip].filter(Boolean).length;
  if (found < 2) return null;
  return { city, state, zip };
}

/**
 * Floating "View Options" control.
 *  - Desktop (>=md): expands as a dropdown card anchored to the top-right button.
 *  - Mobile (<md):   expands as a full-width bottom sheet so it doesn't obscure
 *                    the 3D house.
 * Contains: Time of Day, Floor / Section-Cut, Build Location, Tour + Reel.
 */
export default function ViewOptionsPanel({
  simpleMode = false,
  timeOfDay,
  onTimeOfDayChange,
  floorLevels = [],
  visibleFloors = [],
  onVisibleFloorsChange,
  onPlayTour,
  onExportReel,
  tourRunning = false,
  reelExporting = false,
  buildSiteMetadata = null,
  onSaveLocation,
  onEnhance,
  enhanceLoading = false,
  showInteriorWalls = true,
  onToggleInteriorWalls,
  xrayInteriorWalls = false,
  onToggleXrayInteriorWalls,
  draftingMode = false,
  onToggleDraftingMode,
  onSnapTopDown,
  onResetView,
  onRerecordCover,
  rerecordingCover = false,
  measurementSystem = "imperial",
  onChangeMeasurementSystem,
}) {
  const [open, setOpen] = useState(false);
  // V1.9.1 — make the View Options toggle draggable. Default origin
  // stays top-right (controlled by the parent absolute classes); the
  // hook adds an x/y delta on top, persisted via localStorage so the
  // user's position survives reloads.
  const viewOptsDrag = useDraggable("view-options-btn", { x: 0, y: 0 });
  const hh = String(Math.floor(timeOfDay)).padStart(2, "0");
  const mm = String(Math.floor((timeOfDay % 1) * 60)).padStart(2, "0");

  const [city, setCity] = useState(buildSiteMetadata?.city || "");
  const [stateCode, setStateCode] = useState(buildSiteMetadata?.state || "");
  const [zip, setZip] = useState(buildSiteMetadata?.zip_code || "");
  const [savingLoc, setSavingLoc] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [mapCoords, setMapCoords] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);

  // Keep local inputs in sync when the parent's metadata updates (e.g., on load)
  useEffect(() => {
    setCity(buildSiteMetadata?.city || "");
    setStateCode(buildSiteMetadata?.state || "");
    setZip(buildSiteMetadata?.zip_code || "");
  }, [buildSiteMetadata]);

  // Geocode the saved ZIP via our backend proxy (server-side Nominatim with
  // Mongo caching + 30-day TTL). The proxy avoids browser CORS surprises and
  // shields users from Nominatim rate limits. Cached on the saved zip — typing
  // into the input does NOT trigger a fetch until the user presses Save Location.
  useEffect(() => {
    const z = buildSiteMetadata?.zip_code;
    if (!z || z.length !== 5) {
      setMapCoords(null);
      return undefined;
    }
    let cancelled = false;
    setMapLoading(true);
    const country = (buildSiteMetadata?.country || "US").toUpperCase();
    geocodeZip(z, country, buildSiteMetadata?.city || "", buildSiteMetadata?.state || "")
      .then((r) => {
        if (cancelled) return;
        const { lat, lon } = r.data || {};
        if (typeof lat === "number" && typeof lon === "number") {
          setMapCoords({ lat, lon });
        } else {
          setMapCoords(null);
        }
      })
      .catch(() => { if (!cancelled) setMapCoords(null); })
      .finally(() => { if (!cancelled) setMapLoading(false); });
    return () => { cancelled = true; };
  }, [buildSiteMetadata?.zip_code, buildSiteMetadata?.country]);

  const locLabel =
    buildSiteMetadata?.label ||
    [buildSiteMetadata?.city, buildSiteMetadata?.state, buildSiteMetadata?.zip_code]
      .filter(Boolean).join(", ") ||
    "Not set";

  const dirty =
    (city || "").trim() !== (buildSiteMetadata?.city || "") ||
    (stateCode || "").trim() !== (buildSiteMetadata?.state || "") ||
    (zip || "").trim() !== (buildSiteMetadata?.zip_code || "");

  const handleSaveLocation = async () => {
    if (!onSaveLocation || !dirty) return;
    setSavingLoc(true);
    try {
      const parts = [city.trim(), stateCode.trim(), zip.trim()].filter(Boolean);
      const label = parts.length ? parts.join(", ").replace(/,\s*(\d{5})$/, " $1") : null;
      // V2.0.58 — If the user gave us a ZIP but no state, auto-derive
      // the state from our /code-finder/lookup endpoint so the Build
      // Cost estimator's regional multiplier picks up the right region.
      let effectiveState = stateCode.trim();
      if (zip.trim().length === 5 && !effectiveState) {
        try {
          const r = await fetch(`${BACKEND_URL || ""}/api/code-finder/lookup?zip=${encodeURIComponent(zip.trim())}`);
          if (r.ok) {
            const j = await r.json();
            const inferred = (j?.summary?.state || j?.state || "").trim().toUpperCase();
            if (inferred) {
              effectiveState = inferred;
              setStateCode(inferred);
            }
          }
        } catch { /* non-fatal — user can still type the state manually */ }
      }
      // V1.9.8 — persist lat/lon alongside the address so the open-meteo
      // energy-score path (`routers/energy_score.py`) can fetch real
      // degree-days for this build site without an extra geocode hop.
      // `mapCoords` was already fetched by the geocode useEffect above
      // for the mini-map; we just forward it.
      await onSaveLocation({
        city: city.trim() || undefined,
        state: effectiveState || undefined,
        zip_code: zip.trim() || undefined,
        country: "US",
        label,
        lat: mapCoords?.lat,
        lon: mapCoords?.lon,
      });
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1800);
    } finally {
      setSavingLoc(false);
    }
  };

  // Apply a parsed address to the three inputs at once, but only override a
  // field that we successfully extracted (so partial fixes don't blank out
  // existing data).
  const applyParsed = (parsed) => {
    if (!parsed) return false;
    if (parsed.city) setCity(parsed.city);
    if (parsed.state) setStateCode(parsed.state);
    if (parsed.zip) setZip(parsed.zip);
    return true;
  };

  // Triggers when the user pastes into ANY of the three inputs. If the pasted
  // text looks like a full address, intercept it and split into all 3 fields.
  const handleAddressPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    const parsed = parseAddressString(text);
    if (parsed) {
      e.preventDefault();
      applyParsed(parsed);
    }
  };

  // City onChange: auto-split if the user finishes typing a comma-separated
  // address. Triggers ONLY when both a comma and at least one state token or
  // ZIP appear, to avoid splitting partial keystrokes.
  const handleCityChange = (e) => {
    const v = e.target.value;
    const looksFull = /,/.test(v) && (/\b\d{5}\b/.test(v) || /\b[A-Za-z]{2}\b\s*\d?/.test(v.split(",").pop() || ""));
    if (looksFull) {
      const parsed = parseAddressString(v);
      if (parsed) {
        applyParsed(parsed);
        return;
      }
    }
    setCity(v);
  };

  // Panel body — same for both desktop dropdown & mobile sheet.
  const body = (
    <div className="p-4 space-y-5">
      {/* V1.7 — Simple Mode banner. The single-line explanation we owe
          plain-talk users when they peek at this advanced panel.
          V1.9.1 — Now also shows the LOCK pill since Simple Mode is the
          only available mode (architect mode is gated behind a future
          subscription). */}
      {simpleMode && (
        <div
          data-testid="view-options-simple-banner"
          className="border border-brass-700 bg-brass-950/40 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-brass-200"
        >
          <div className="flex items-center justify-between gap-2">
            <span>// Simple Mode</span>
            <span
              data-testid="simple-mode-locked-pill"
              title="Architect Mode unlocks with a paid subscription (coming soon)"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-amber-500 bg-amber-950/60 text-amber-300 text-[9px]"
            >
              <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="7" width="10" height="7" rx="1" />
                <path d="M5 7V5a3 3 0 0 1 6 0v2" />
              </svg>
              LOCKED
            </span>
          </div>
          <p className="text-zinc-400 normal-case tracking-normal text-[11px] mt-1">
            Architect Mode (CAD drafting, x-ray walls, cinematic tour, time-of-day) unlocks with a paid subscription — coming soon.
          </p>
        </div>
      )}

      {/* V1.9.3 — Measurement system toggle. Imperial (US) vs Metric (EU).
          Drives grid-snap step (1ft vs 25cm) for drag/drop placements and
          the dimension labels on rooms + the Wall Type Schedule. */}
      <div
        data-testid="measurement-system-toggle"
        className="border border-zinc-800 px-3 py-2"
      >
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
          // Units
        </div>
        <div className="inline-flex border border-zinc-700">
          <button
            type="button"
            data-testid="units-imperial"
            onClick={() => onChangeMeasurementSystem?.("imperial")}
            aria-pressed={measurementSystem === "imperial"}
            className={
              "px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors " +
              (measurementSystem === "imperial"
                ? "bg-brass-600 text-zinc-950"
                : "text-zinc-300 hover:text-zinc-100")
            }
          >
            Imperial · ft/in
          </button>
          <button
            type="button"
            data-testid="units-metric"
            onClick={() => onChangeMeasurementSystem?.("metric")}
            aria-pressed={measurementSystem === "metric"}
            className={
              "px-3 py-1 font-mono text-[10px] uppercase tracking-widest border-l border-zinc-700 transition-colors " +
              (measurementSystem === "metric"
                ? "bg-brass-600 text-zinc-950"
                : "text-zinc-300 hover:text-zinc-100")
            }
          >
            Metric · cm/m
          </button>
        </div>
        <p className="text-zinc-500 text-[10px] mt-1.5">
          {measurementSystem === "imperial"
            ? "Drag-snap to 1' grid · 16\" cabinet/appliance alignment"
            : "Drag-snap to 25 cm grid · 60 cm module alignment"}
        </p>
      </div>
      {/* V1.8.2 — Reset View. Available in both Simple + Architect modes.
          Re-runs the auto-fit-to-front so users can recover from awkward
          orbits without reloading the page. */}
      {onResetView && (
        <section data-testid="view-options-reset" className="space-y-2">
          <button
            type="button"
            data-testid="reset-view-btn"
            onClick={() => onResetView()}
            className="inline-flex items-center gap-2 w-full justify-between font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-brass-500 hover:bg-brass-950/30 bg-zinc-900 transition-colors"
          >
            <span className="inline-flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" />
              // Reset View
            </span>
            <span className="text-zinc-500">snap →</span>
          </button>
          {onRerecordCover && (
            <button
              type="button"
              data-testid="rerecord-cover-btn"
              onClick={() => onRerecordCover()}
              disabled={rerecordingCover}
              className={
                "inline-flex items-center gap-2 w-full justify-between font-mono text-[10px] uppercase tracking-widest px-3 py-2 border bg-zinc-900 transition-colors " +
                (rerecordingCover
                  ? "border-zinc-700 text-zinc-500 cursor-wait"
                  : "border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-fuchsia-500 hover:bg-fuchsia-950/30")
              }
            >
              <span className="inline-flex items-center gap-1.5">
                {rerecordingCover ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Film className="w-3.5 h-3.5" />
                )}
                // {rerecordingCover ? "Recording..." : "Re-record Cover"}
              </span>
              <span className="text-zinc-500">{rerecordingCover ? "" : "3s →"}</span>
            </button>
          )}
        </section>
      )}

      {/* Time of Day */}
      {!simpleMode && (
      <section data-testid="view-options-tod">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-brass-400">
            <Sun className="w-3 h-3" /> // Time of Day
          </span>
          <span className="font-mono text-[10px] text-zinc-100">
            {hh}:{mm}
          </span>
        </div>
        <input
          type="range" min="0" max="24" step="0.25"
          value={timeOfDay}
          onChange={(e) => onTimeOfDayChange(parseFloat(e.target.value))}
          data-testid="time-of-day-input"
          className="w-full accent-brass-400"
        />
        <div className="flex justify-between font-mono text-[9px] text-zinc-500 mt-1 uppercase tracking-widest">
          <span>night</span><span>dawn</span><span>noon</span><span>dusk</span><span>night</span>
        </div>
      </section>
      )}

      {/* Floor / X-Ray — V1.8.5: also available in Simple Mode so users
          can see through the top floor on multi-story plans. */}
      {floorLevels.length > 1 && (
        <section data-testid="view-options-floors">
          <div className="flex items-center justify-between mb-2">
            <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-brass-400">
              <Layers className="w-3 h-3" /> // Floors / Section-Cut
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1" data-testid="floor-selector">
            {floorLevels.map((lv) => {
              const active = visibleFloors.length > 0 && Number(visibleFloors[0]) === lv;
              return (
                <button
                  key={lv}
                  onClick={() => onVisibleFloorsChange([lv])}
                  data-testid={`floor-btn-${lv}`}
                  className={
                    "font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 transition-colors " +
                    (active
                      ? "bg-brass-500 text-zinc-950"
                      : "bg-zinc-900 text-zinc-300 hover:text-zinc-50 hover:bg-zinc-800")
                  }
                >
                  [{lv}]
                </button>
              );
            })}
            <button
              onClick={() => onVisibleFloorsChange([])}
              data-testid="floor-btn-all"
              className={
                "font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 transition-colors " +
                (visibleFloors.length === 0
                  ? "bg-brass-500 text-zinc-950"
                  : "bg-zinc-900 text-zinc-300 hover:text-zinc-50 hover:bg-zinc-800")
              }
            >
              [All]
            </button>
          </div>
        </section>
      )}

      {/* Interior wall toggle — flips visibility of non-perimeter walls so big
          designs stay snappy, and exterior shell views read cleanly. */}
      {!simpleMode && (onToggleInteriorWalls || onToggleXrayInteriorWalls) && (
        <section data-testid="view-options-interior-walls" className="space-y-2">
          {onToggleInteriorWalls && (
            <button
              type="button"
              data-testid="interior-walls-toggle"
              onClick={() => onToggleInteriorWalls(!showInteriorWalls)}
              aria-pressed={!showInteriorWalls}
              className={
                "inline-flex items-center gap-2 w-full justify-between font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " +
                (showInteriorWalls
                  ? "border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-zinc-500 bg-zinc-900"
                  : "border-brass-500 text-brass-300 bg-brass-950/30")
              }
            >
              <span className="inline-flex items-center gap-1.5">
                {showInteriorWalls
                  ? <Eye className="w-3.5 h-3.5" />
                  : <EyeOff className="w-3.5 h-3.5" />}
                // Interior Walls
              </span>
              <span className={showInteriorWalls ? "text-zinc-500" : "text-brass-300"}>
                {showInteriorWalls ? "shown" : "hidden"}
              </span>
            </button>
          )}
          {onToggleXrayInteriorWalls && (
            <button
              type="button"
              data-testid="xray-walls-toggle"
              onClick={() => onToggleXrayInteriorWalls(!xrayInteriorWalls)}
              aria-pressed={xrayInteriorWalls}
              disabled={!showInteriorWalls}
              title={!showInteriorWalls ? "Re-enable Interior Walls to use X-Ray" : ""}
              className={
                "inline-flex items-center gap-2 w-full justify-between font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed " +
                (xrayInteriorWalls
                  ? "border-fuchsia-500 text-fuchsia-300 bg-fuchsia-950/30"
                  : "border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-zinc-500 bg-zinc-900")
              }
            >
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                // X-Ray Walls
              </span>
              <span className={xrayInteriorWalls ? "text-fuchsia-300" : "text-zinc-500"}>
                {xrayInteriorWalls ? "smart fade" : "off"}
              </span>
            </button>
          )}
          {onToggleXrayInteriorWalls && xrayInteriorWalls && (
            <p className="font-mono text-[9px] text-zinc-500 leading-relaxed pl-1">
              Walls fade as you approach for clearer interior navigation.
            </p>
          )}
        </section>
      )}

      {/* V1.6 — Drafting Mode (Whiteprint aesthetic) + Top-Down 2D snap */}
      {!simpleMode && (onToggleDraftingMode || onSnapTopDown) && (
        <section data-testid="view-options-drafting" className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-brass-400">
              <Ruler className="w-3 h-3" /> // Drafting Suite
            </span>
          </div>
          {onToggleDraftingMode && (
            <button
              type="button"
              data-testid="drafting-mode-toggle"
              onClick={() => onToggleDraftingMode(!draftingMode)}
              aria-pressed={draftingMode}
              className={
                "inline-flex items-center gap-2 w-full justify-between font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " +
                (draftingMode
                  ? "border-brass-400 text-brass-100 bg-brass-700/40"
                  : "border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-zinc-500 bg-zinc-900")
              }
            >
              <span className="inline-flex items-center gap-1.5">
                <SquareIcon className="w-3.5 h-3.5" />
                // Drafting Mode
              </span>
              <span className={draftingMode ? "text-brass-100" : "text-zinc-500"}>
                {draftingMode ? "engaged" : "off"}
              </span>
            </button>
          )}
          {onSnapTopDown && (
            <button
              type="button"
              data-testid="top-down-snap-btn"
              onClick={() => onSnapTopDown()}
              className="inline-flex items-center gap-2 w-full justify-between font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-brass-500 hover:bg-brass-950/30 bg-zinc-900 transition-colors"
            >
              <span className="inline-flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5" />
                // Top-Down 2D
              </span>
              <span className="text-zinc-500">snap →</span>
            </button>
          )}
          {draftingMode && (
            <p className="font-mono text-[9px] text-brass-300/80 leading-relaxed pl-1">
              Visualization simplified for technical precision.
            </p>
          )}
        </section>
      )}

      {/* Build Location */}
      {!simpleMode && (
      <section data-testid="view-options-location">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-brass-400">
            <MapPin className="w-3 h-3" /> // Build Location
          </span>
          <span className="font-mono text-[10px] text-zinc-400" data-testid="location-current">
            {locLabel}
          </span>
        </div>
        <div className="grid grid-cols-12 gap-1.5">
          <input
            value={city}
            onChange={handleCityChange}
            onPaste={handleAddressPaste}
            placeholder="City — or paste full address"
            data-testid="location-city-input"
            className="col-span-7 bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-2.5 py-2 font-mono text-xs text-zinc-100"
          />
          <input
            value={stateCode}
            onChange={(e) => setStateCode(e.target.value.toUpperCase().slice(0, 2))}
            onPaste={handleAddressPaste}
            placeholder="ST"
            maxLength={2}
            data-testid="location-state-input"
            className="col-span-2 bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-2 py-2 font-mono text-xs text-zinc-100 text-center"
          />
          <input
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
            onPaste={handleAddressPaste}
            placeholder="ZIP"
            inputMode="numeric"
            data-testid="location-zip-input"
            className="col-span-3 bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-2 py-2 font-mono text-xs text-zinc-100"
          />
        </div>
        {onSaveLocation && (
          <button
            type="button"
            onClick={handleSaveLocation}
            disabled={(!dirty && !justSaved) || savingLoc}
            data-testid="location-save-btn"
            className={
              "mt-2 w-full inline-flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " +
              (justSaved
                ? "border-emerald-500 text-emerald-300 bg-emerald-950/40"
                : dirty && !savingLoc
                ? "border-brass-500 bg-brass-500 text-zinc-950 hover:bg-brass-400"
                : "border-zinc-800 text-zinc-500 cursor-not-allowed")
            }
          >
            {savingLoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : justSaved ? <Check className="w-3.5 h-3.5" />
              : <Save className="w-3.5 h-3.5" />}
            {savingLoc ? "Saving..." : justSaved ? "Saved" : "Save Location"}
          </button>
        )}
        <p className="font-mono text-[9px] text-zinc-500 mt-1.5 leading-relaxed">
          Climate-aware AI defaults: solar orientation, materials, roof pitch.
        </p>

        {/* OpenStreetMap mini map — only renders once a ZIP is saved + geocoded. */}
        {(mapCoords || mapLoading) && (
          <div
            data-testid="location-map-widget"
            className="mt-3 border border-zinc-800 bg-zinc-900 overflow-hidden"
          >
            <div className="px-2.5 py-1.5 border-b border-zinc-800 bg-zinc-950/60 flex items-center justify-between">
              <span className="font-mono text-[9px] uppercase tracking-widest text-brass-400/80">
                // Site Map
              </span>
              {mapLoading && <Loader2 className="w-3 h-3 animate-spin text-brass-400" />}
            </div>
            {mapCoords ? (
              <iframe
                title="Site Location Map"
                data-testid="location-map-iframe"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapCoords.lon - 0.04},${mapCoords.lat - 0.025},${mapCoords.lon + 0.04},${mapCoords.lat + 0.025}&layer=mapnik&marker=${mapCoords.lat},${mapCoords.lon}`}
                className="w-full h-36 block bg-zinc-800"
                style={{ border: 0, filter: "saturate(0.85) brightness(0.92)" }}
                loading="lazy"
              />
            ) : (
              <div className="h-36 grid place-items-center font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                Resolving postal code...
              </div>
            )}
            {mapCoords && (
              <a
                href={`https://www.openstreetmap.org/?mlat=${mapCoords.lat}&mlon=${mapCoords.lon}#map=13/${mapCoords.lat}/${mapCoords.lon}`}
                target="_blank"
                rel="noreferrer"
                data-testid="location-map-fullscreen"
                className="block px-2.5 py-1 border-t border-zinc-800 bg-zinc-950/60 font-mono text-[9px] uppercase tracking-widest text-zinc-400 hover:text-brass-300"
              >
                // Open in OpenStreetMap →
              </a>
            )}
          </div>
        )}
      </section>
      )}

      {/* Realistic AI Render (placeholder — wires snapshot + 'Coming Soon' toast) */}
      {!simpleMode && onEnhance && (
        <section data-testid="view-options-enhance" className="space-y-2">
          <button
            type="button"
            data-testid="enhance-render-btn"
            onClick={onEnhance}
            disabled={enhanceLoading}
            className="inline-flex items-center gap-2 w-full justify-center bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 transition-colors"
          >
            {enhanceLoading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Sparkles className="w-3.5 h-3.5" />}
            {enhanceLoading ? "// Capturing hero shot..." : "// Generate Realistic View"}
          </button>
          <p className="font-mono text-[9px] text-zinc-500 leading-relaxed">
            Captures a 2K hero-angle render of your design — high-fidelity AI overlay coming soon.
          </p>
        </section>
      )}

      {/* Cinematic Tour */}
      {!simpleMode && onPlayTour && (
        <section data-testid="view-options-tour" className="space-y-2">
          <button
            type="button"
            data-testid="play-tour-btn"
            onClick={onPlayTour}
            disabled={tourRunning || reelExporting}
            className="inline-flex items-center gap-2 w-full justify-center bg-orange-600 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 transition-colors"
          >
            {tourRunning && !reelExporting
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Play className="w-3.5 h-3.5" />}
            {tourRunning && !reelExporting ? "// Touring..." : "// Play Cinematic Tour"}
          </button>
          {onExportReel && (
            <button
              type="button"
              data-testid="export-reel-btn"
              onClick={onExportReel}
              disabled={tourRunning || reelExporting}
              className="inline-flex items-center gap-2 w-full justify-center bg-zinc-900 border border-brass-500/40 hover:border-brass-500 hover:bg-brass-950/30 disabled:opacity-50 disabled:cursor-not-allowed text-brass-300 font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 transition-colors"
            >
              {reelExporting
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Download className="w-3.5 h-3.5" />}
              {reelExporting ? "// Stitching..." : "// Download Reel (.gif)"}
            </button>
          )}
        </section>
      )}
    </div>
  );

  return (
    <>
      {/* Toggle button — stays top-right on both layouts. V1.9.1: draggable
          via the cyan grip on the left so power users can move it out of
          the canvas area. The container uses transform: translate3d so the
          drag offset compounds with the absolute anchor without breaking
          its initial top-right position.
          V2.0.4 — Hidden on phones (<=640px) since the canvas-actions ••• 
          overflow already covers Imagine/Code Check/Energy, and the
          floor / interior-wall / time-of-day toggles are 3D-only (phones
          lock to PLAN view per the mobile scope). */}
      <div
        className="absolute top-4 right-4 z-30 hidden sm:flex items-center"
        data-testid="view-options-dock"
        {...viewOptsDrag.containerProps}
      >
        <span
          data-testid="view-options-drag-handle"
          title="Drag to reposition"
          aria-label="Drag the View Options button"
          className="inline-flex items-center justify-center w-5 h-7 border-r-0 border border-zinc-700 bg-zinc-950/80 text-zinc-400 hover:bg-zinc-900 hover:text-brass-300 select-none backdrop-blur"
          {...viewOptsDrag.handleProps}
        >
          <svg width="8" height="14" viewBox="0 0 6 12" fill="currentColor">
            <circle cx="1.5" cy="1.5" r="1" />
            <circle cx="4.5" cy="1.5" r="1" />
            <circle cx="1.5" cy="6"   r="1" />
            <circle cx="4.5" cy="6"   r="1" />
            <circle cx="1.5" cy="10.5" r="1" />
            <circle cx="4.5" cy="10.5" r="1" />
          </svg>
        </span>
        <div className="flex flex-col items-end">
          <button
            type="button"
            data-testid="view-options-toggle"
            onClick={() => setOpen((o) => !o)}
            className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors ${
              open
                ? "border-brass-500 text-brass-300 bg-brass-950/40"
                : "border-zinc-700 text-zinc-300 bg-zinc-950/80 backdrop-blur hover:border-brass-500/60 hover:text-brass-300"
            }`}
            aria-expanded={open}
          >
            {open ? <X className="w-3.5 h-3.5" /> : <Settings2 className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">// View Options</span>
            <span className="sm:hidden">View</span>
          </button>

          {/* Desktop dropdown (hidden on < md) */}
          {open && (
            <div
              data-testid="view-options-panel"
              className="hidden md:block mt-2 w-80 max-w-[calc(100vw-2rem)] border border-zinc-800 bg-zinc-950/92 backdrop-blur-md shadow-[0_12px_48px_rgba(0,0,0,0.6)]"
              style={{ paddingBottom: "max(0rem, env(safe-area-inset-bottom))" }}
            >
              {body}
            </div>
          )}
        </div>
      </div>

      {/* Mobile bottom sheet (hidden on >= md) */}
      {open && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-zinc-950/50 backdrop-blur-sm z-30 animate-in fade-in duration-200"
            onClick={() => setOpen(false)}
            data-testid="view-options-scrim"
          />
          <div
            data-testid="view-options-sheet"
            className="md:hidden fixed left-0 right-0 bottom-0 z-40 bg-zinc-950 border-t-2 border-brass-500/40 shadow-[0_-12px_48px_rgba(0,0,0,0.7)] max-h-[80dvh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
          >
            <div className="sticky top-0 bg-zinc-950/95 backdrop-blur-md flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <span className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
                // View Options
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-zinc-400 hover:text-zinc-100 p-1"
                aria-label="Close"
                data-testid="view-options-sheet-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {body}
          </div>
        </>
      )}
    </>
  );
}
